import React from "react";
import dayjs from "dayjs";
import { TrendingUp } from "react-feather";
import { useHasPermission } from "../../auth/useClaims";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetCollectionsSummaryQuery } from "../../collections/collectionsApi";
import { useResolvedYesterday, useWorkingDayOnOrBefore } from "../../masters/holidays/useWorkingDay";
import { money, percentChange, sumBy } from "../dashboardHelpers";
import KpiCard from "../components/KpiCard";
import { paths } from "../../../routes/paths";

// "Yesterday" is the last WORKING day (2026-09-14). This card used plain calendar
// dates on purpose, but that meant every Monday it showed Sunday's 0 "vs" Saturday,
// and the day after a public holiday likewise - the opposite of what anyone opening
// the dashboard wants to know. It now shows the last working day (e.g. Saturday on a
// Monday), says so in its caption, and compares with the working day before that.
const YesterdayCollectionKpi = () => {
  const canView = useHasPermission("Collections.View");
  const { selectedBranchId } = useBranchFilter();

  const yesterday = useResolvedYesterday({ skip: !canView });
  const dayBefore = useWorkingDayOnOrBefore(
    dayjs(yesterday.date).subtract(1, "day").format("YYYY-MM-DD"),
    { skip: !canView || yesterday.isResolving }
  );

  const { data: yesterdaySummary, isFetching } = useGetCollectionsSummaryQuery(
    { branchId: selectedBranchId ?? undefined, dateFrom: yesterday.date, dateTo: yesterday.date },
    { skip: !canView || yesterday.isResolving }
  );
  const { data: dayBeforeSummary } = useGetCollectionsSummaryQuery(
    { branchId: selectedBranchId ?? undefined, dateFrom: dayBefore.date, dateTo: dayBefore.date },
    { skip: !canView || yesterday.isResolving || dayBefore.isResolving }
  );

  if (!canView) return null;

  const yesterdayTotal = sumBy(yesterdaySummary, "totalAmount");
  const dayBeforeTotal = sumBy(dayBeforeSummary, "totalAmount");

  return (
    <div className="col-6 col-xl-3">
      <KpiCard
        icon={<TrendingUp size={20} />}
        iconBg="#1B2A4A"
        label="Yesterday's Collection"
        value={isFetching || yesterday.isResolving ? "..." : money(yesterdayTotal)}
        caption={yesterday.shifted ? `${dayjs(yesterday.date).format("ddd D MMM")} · yesterday was ${yesterday.reason}` : undefined}
        trendLabel="vs previous working day"
        trendPercent={percentChange(yesterdayTotal, dayBeforeTotal)}
        currentValue={yesterdayTotal}
        comparisonValue={dayBeforeTotal}
        compareRangeLabel={dayBefore.date}
        // Uses the RESOLVED working day, not literally yesterday - so the list
        // opens on the same day the figure was computed from.
        drillTo={paths.collectionsFiltered({ branchId: selectedBranchId ?? undefined, dateFrom: yesterday.date, dateTo: yesterday.date })}
        drillTitle="See that day's collection records"
      />
    </div>
  );
};

export default YesterdayCollectionKpi;
